/**
 * BROWSER OTLP emit vocabulary -- the wire keys the web producer puts on a logs export, and the ONE builder that
 * composes them.
 *
 * MEMBERSHIP RULE: a key lives here only while a LIVE EMITTER writes it. This leaf is a producer contract, not a
 * semconv catalogue; a member with no emitter is dead vocabulary that reads as documentation of a wire this fleet
 * never puts bytes on. Add the member in the change that adds its emitter, and delete it in the change that removes
 * one -- the same discipline `OTEL_RESOURCE_ATTRS` (`const/otlp-span-protocol.ts`) states for its own re-exports.
 *
 * SEPARATE FROM `const/observability-otel-attrs.ts` ON PURPOSE, on ONE ground: that leaf is bound VERBATIM to the
 * Claude Code harness emitter contract, and this is the BROWSER producer's contract -- two emitters, two membership
 * rules, and a merged record would let a harness-side deletion silently retire a key this producer still writes. The
 * two keys this file does NOT redeclare are exactly the two the QUERY side already reads (`OTEL_ATTRS.EVENT_NAME`,
 * `OTEL_ATTRS.SESSION_ID`): a browser row has to answer the same `event_name` / `session_id` projection as every other
 * row in the Iceberg table, so those are consumed from the harness leaf read-only rather than re-spelled here.
 *
 * THE SECOND GROUND THIS HEADER USED TO CARRY IS RETIRED AS MEASURED-FALSE (2026-08-09). It argued that folding these
 * keys in would widen `QUAL-OTEL-ATTR-KEY-RAW-LITERAL-001`'s alternation "over every harness reader -- manufacturing
 * violations on a vocabulary those readers never carry". That rule now derives its alternation from the UNION of the
 * harness, exception and BROWSER registries, so this leaf's keys are already in it, and the measured live-corpus queue
 * is ZERO: the members are DOTTED, the arms are key-position-anchored, and a harness reader carries no such literal.
 * Separation therefore rests on the contract ground alone, which is the ground that was always doing the work.
 *
 * SPELLINGS ARE OTel SEMCONV WHERE SEMCONV HAS ONE (`enduser.id`, `url.full`, `url.path`, `user_agent.original`,
 * `client.address`, `geo.*`), so a generic collector groups these rows with no custom mapping. The `exception.*` triple
 * is semconv too and is READ from `OTEL_EXCEPTION_ATTRS` (`const/otel-exception-attrs.ts`) rather than re-spelled --
 * the `WORKER_GUARD_ATTRS` (`const/worker-shell-diagnostics.ts`) accessor pattern, so this record keeps the browser-side
 * semantics while the wire bytes have exactly one home. The `browser.*` members are the residue semconv does not cover;
 * they carry this repo's own namespace rather than squatting a semconv prefix that may later be defined to mean
 * something else.
 */

import { LOG_LEVELS } from '@federa/const/logging';
import { ACTIVITY_TYPES } from '@federa/const/observability-activity-types';
import { OTEL_ATTRS } from '@federa/const/observability-otel-attrs';
import { OTEL_EXCEPTION_ATTRS } from '@federa/const/otel-exception-attrs';
import { OTLP_LOG_SEVERITY } from '@federa/const/otlp-log-severity';
import { OTLP_SCOPE_NAMES } from '@federa/const/otlp-scope-names';
import { otlpBoolAttribute, otlpResourceAttributes, unixNanoString } from '@federa/const/otlp-span-protocol';
import { CAMEL_LOWER_UPPER_BOUNDARY_SOURCE } from '@federa/const/string-case';
import { withoutUrlFragment } from '@federa/const/url-fragment';
import type {
	BrowserOtlpAttribute,
	BrowserOtlpLogRecord,
	BrowserOtlpLogsPayload,
	BrowserTelemetryResourceContext,
	TelemetryEvent,
} from '@federa/types/telemetry-browser-events';

/** OTel `service.name` for the web producer. DELIBERATELY NOT the same authority as `TELEMETRY_ASSISTANTS.FEDERA_WEB`, which happens to share its text: this is what the BROWSER claims on the wire, that is what the DOOR stamps server-side after ignoring the claim. Collapsing them onto one const would re-establish exactly the payload trust the door's stamping exists to remove. */
const BROWSER_TELEMETRY_SERVICE_NAME = 'federa-web';

/** `event.name` for a Web Vitals measurement. A DEDICATED name rather than the generic `performance` activity type: the three vitals share one record shape (`name` + `value` + path) that no other browser event has, and a reader filtering the table needs to select them without enumerating metric names. */
export const BROWSER_WEB_VITAL_EVENT_NAME = 'browser.web_vital';

/** Wire keys this producer emits. Every member is reached by the builder below; see the header's membership rule. */
export const BROWSER_TELEMETRY_ATTRS = {
	/** Authenticated end-user id, present ONLY once `setUserId` has run. Absent on every pre-login record, which is a real distinction and not a gap. */
	ENDUSER_ID: 'enduser.id',
	// The semconv exception triple, READ from its own leaf rather than re-spelled -- the isolate guard stamps the same
	// three keys, and two hand-typed copies of an upstream convention is exactly the drift a shared home closes.
	EXCEPTION_TYPE: OTEL_EXCEPTION_ATTRS.TYPE,
	EXCEPTION_MESSAGE: OTEL_EXCEPTION_ATTRS.MESSAGE,
	EXCEPTION_STACKTRACE: OTEL_EXCEPTION_ATTRS.STACKTRACE,
	/** Full page url of the batch, on the RESOURCE. */
	URL_FULL: 'url.full',
	/** Path only, on a RECORD -- the low-cardinality dimension a rollup may group by, which `url.full` (query string, ids) is not. */
	URL_PATH: 'url.path',
	USER_AGENT_ORIGINAL: 'user_agent.original',
	VIEWPORT_WIDTH: 'browser.viewport.width',
	VIEWPORT_HEIGHT: 'browser.viewport.height',
	/** True only on the beacon drained by `beforeunload`/`pagehide`. */
	UNLOAD: 'browser.unload',
	WEB_VITAL_NAME: 'browser.web_vital.name',
	WEB_VITAL_VALUE: 'browser.web_vital.value',
	/** Request-derived, stamped SERVER-side by the ingest door from CF headers -- never sent by the client. */
	CLIENT_ADDRESS: 'client.address',
	GEO_COUNTRY_ISO: 'geo.country.iso_code',
	GEO_CITY: 'geo.locality.name',
} as const;

/**
 * The `data` keys promoted onto `exception.*` on an error record, so they are SINGLE-SPELLED on the wire.
 *
 * Load-bearing as a skip list, not just as a mapping: without it the same text ships twice under two keys (`message`
 * AND `exception.message`), and a reader has no way to know they are one value -- so a dashboard counting distinct
 * error messages double-counts every one of them.
 */
const EXCEPTION_DATA_KEYS: ReadonlySet<string> = new Set(['errorType', 'message', 'stack']);

/** camelCase word boundary, for the single-spelling normalisation below. Compiled from the shared
 *  {@link CAMEL_LOWER_UPPER_BOUNDARY_SOURCE} rather than respelled: a `/g` source is shared as a STRING plus a
 *  per-consumer instance, so no `lastIndex` crosses walkers. */
const CAMEL_BOUNDARY_RE = new RegExp(CAMEL_LOWER_UPPER_BOUNDARY_SOURCE, 'g');

/**
 * A `data` key as its snake_case wire spelling.
 *
 * ONE spelling per fact is the whole point: the Iceberg attribute bag is schemaless, so `errorType` and `error_type`
 * become two columns in every reader's mental model and neither is complete. Already-dotted semconv keys carry no
 * uppercase and pass through untouched, which is why the emitter can hand this function everything without a branch.
 */
function snakeKey(key: string): string {
	return key.replace(CAMEL_BOUNDARY_RE, '$1_$2').toLowerCase();
}

/**
 * One AnyValue arm chosen from the value's RUNTIME type, or `null` for a value that must not be emitted.
 *
 * An absent, blank or non-finite value is DROPPED rather than emitted empty, matching `otlpStringAttributes`
 * (`const/otlp-span-protocol.ts`) and for its stated reason: on the wire a key present with `''` is indistinguishable
 * from a real empty value, so a reader cannot tell "the producer had nothing" from "the field was empty".
 */
function attributeFor(key: string, value: unknown): BrowserOtlpAttribute | null {
	if (typeof value === 'string') return value === '' ? null : { key, value: { stringValue: value } };
	if (typeof value === 'number') return Number.isFinite(value) ? { key, value: { doubleValue: value } } : null;
	if (typeof value === 'boolean') return otlpBoolAttribute(key, value);
	if (value === null || value === undefined) return null;
	try {
		return { key, value: { stringValue: JSON.stringify(value) } };
	} catch {
		// Silent: a cyclic or non-serialisable value is dropped rather than failing the batch -- telemetry must never
		// break the page it measures, and the alternative (throwing out of a click handler) is strictly worse.
		return null;
	}
}

/** Appends the attribute when `attributeFor` yields one. */
function pushAttribute(into: BrowserOtlpAttribute[], key: string, value: unknown): void {
	const attribute = attributeFor(key, value);
	if (attribute !== null) into.push(attribute);
}

/** The `exception.*` triple promoted off an error event's data bag. */
function pushExceptionAttributes(into: BrowserOtlpAttribute[], data: Record<string, unknown>): void {
	pushAttribute(into, BROWSER_TELEMETRY_ATTRS.EXCEPTION_TYPE, data.errorType);
	pushAttribute(into, BROWSER_TELEMETRY_ATTRS.EXCEPTION_MESSAGE, data.message);
	pushAttribute(into, BROWSER_TELEMETRY_ATTRS.EXCEPTION_STACKTRACE, data.stack);
}

/** One queued event as one OTLP LogRecord. */
function logRecordFor(event: TelemetryEvent): BrowserOtlpLogRecord {
	const isError = event.type === ACTIVITY_TYPES.ERROR;
	const attributes: BrowserOtlpAttribute[] = [];
	pushAttribute(attributes, OTEL_ATTRS.EVENT_NAME, event.type);
	pushAttribute(attributes, OTEL_ATTRS.SESSION_ID, event.sessionId);
	pushAttribute(attributes, BROWSER_TELEMETRY_ATTRS.ENDUSER_ID, event.userId);
	if (isError) pushExceptionAttributes(attributes, event.data);
	for (const [key, value] of Object.entries(event.data)) {
		if (isError && EXCEPTION_DATA_KEYS.has(key)) continue;
		pushAttribute(attributes, snakeKey(key), value);
	}
	return {
		// Through the ms->nanosecond SSOT, which is STRING concatenation rather than `ms * 1e6`: the multiply silently
		// loses precision above 2^53 and would put a rounded timestamp on the wire with nothing reporting it.
		timeUnixNano: unixNanoString(event.timestamp),
		// Severity numbers are OTLP SPEC values, so they are read from `OTLP_LOG_SEVERITY` (`const/otlp-log-severity.ts`)
		// and never re-declared here. The ternary IS the membership rule this producer used to state as a two-member
		// record: only INFO and ERROR are reachable, because those are the only two bands a browser event maps onto.
		severityNumber: OTLP_LOG_SEVERITY[isError ? LOG_LEVELS.ERROR : LOG_LEVELS.INFO].severityNumber,
		attributes,
	};
}

/** The Resource attribute list: the semconv `service.name` pair through its own SSOT accessor, plus the browser-instance facts. */
function resourceAttributesFor(context: BrowserTelemetryResourceContext): BrowserOtlpAttribute[] {
	// `deploymentEnvironment` is blank on purpose and the accessor DROPS blank pairs: the browser cannot know which
	// fleet environment served it, and guessing from the hostname would put a wrong answer on every record.
	const attributes: BrowserOtlpAttribute[] = [
		...otlpResourceAttributes({ serviceName: BROWSER_TELEMETRY_SERVICE_NAME, deploymentEnvironment: '' }),
	];
	// Stripped HERE, in the one builder both browser senders share, so neither can ship a fragment-borne credential.
	pushAttribute(attributes, BROWSER_TELEMETRY_ATTRS.URL_FULL, withoutUrlFragment(context.url));
	pushAttribute(attributes, BROWSER_TELEMETRY_ATTRS.USER_AGENT_ORIGINAL, context.userAgent);
	if (context.viewport) {
		pushAttribute(attributes, BROWSER_TELEMETRY_ATTRS.VIEWPORT_WIDTH, context.viewport.width);
		pushAttribute(attributes, BROWSER_TELEMETRY_ATTRS.VIEWPORT_HEIGHT, context.viewport.height);
	}
	if (context.unload === true) pushAttribute(attributes, BROWSER_TELEMETRY_ATTRS.UNLOAD, true);
	return attributes;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): ONE OTLP/HTTP JSON `ExportLogsServiceRequest` for a batch of
 * queued browser events.
 *
 * THE SINGLE EMITTER of every key above, and deliberately shared by BOTH browser producers -- the `TelemetryProvider`
 * queue and the provider-less `trackComponentError` sender. A second builder is what let the old transport's flush and
 * unload paths diverge unnoticed, and the same divergence across two FILES would be invisible to any single test.
 */
export function buildBrowserOtlpLogsPayload(
	events: readonly TelemetryEvent[],
	context: BrowserTelemetryResourceContext,
): BrowserOtlpLogsPayload {
	return {
		resourceLogs: [
			{
				resource: { attributes: resourceAttributesFor(context) },
				scopeLogs: [{ scope: { name: OTLP_SCOPE_NAMES.WEB_TELEMETRY }, logRecords: events.map(logRecordFor) }],
			},
		],
	};
}
